import React, { useState } from 'react';

import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import { useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useUserHook from 'hooks/useUserHook';

import { useUserStore } from 'store/user.store';

import { useMutationCustomerSet } from 'api/mutations/customerSet.mutation';
import { useQueryCustomerGet } from 'api/queries/customerGet.query';
import { useQueryGetOpcProjectsList } from 'api/queries/getOpcProjectsList.query';

import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import { LoaderSpinner } from 'components/common/LodaerSpinner/LoaderSpinner';

import { buildCustomerPayload } from 'components/Settings/Components/Opc/OpcUtils';
import { settingsConfig } from 'components/Settings/settingsConfig';
import { Button, ConfirmSaveModal } from 'components/common';

import classes from './OpcConnectionInfo.module.scss';

export const OpcConnectionInfo = () => {
    const updateUserStore = useUserStore((state) => state.updateUser);

    const history = useHistory();
    const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
    const { isLoading, isSuccess, refetch } = useQueryGetOpcProjectsList();
    const { customerId } = useUserHook();
    const { data: customerData } = useQueryCustomerGet({ customerId });
    const { mutate, isLoading: isDisconnectSubmitting } = useMutationCustomerSet();
    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();

    const onReplaceCredentials = (): void => {
        history.push(
            AppRoutes.buildPath(
                AppRoutes.SETTINGS,
                settingsConfig.integrations.link,
                settingsConfig.opc.link,
                settingsConfig.editOpc.link,
            ),
        );
    };

    const handleConfirmDisconnectOpc = (): void => {
        if (!customerId || !customerData) {
            return;
        }

        // Same payload of customer without OPC details passed.
        const customerPayload = buildCustomerPayload({ customerId, customerData });

        mutate(customerPayload, {
            onSuccess: () => {
                //Clear cached OPC projects list to avoid showing stale data in case of disconnection and reconnection.
                queryClient.removeQueries('getOpcProjectsList');
                updateUserStore({ opcEnabled: false });
                setIsDisconnectModalOpen(false);
                handleSnackBar('Successfully disconnected Oracle Primavera Cloud integration.', 'success');
                history.push(AppRoutes.buildPath(AppRoutes.SETTINGS, settingsConfig.integrations.link));
            },
            onError: () => {
                handleSnackBar('Failed to disconnect Oracle Primavera Cloud integration.', 'error');
            },
        });
    };

    const shouldShowConnectedState = !isLoading && isSuccess;
    const shouldShowConnectionIssueState = !isLoading && !isSuccess;

    return (
        <div className={classes.OpcConnectionInfo}>
            <div className={classes.details}>
                <div className={classes.detailsColumn}>
                    <div className={classes.detailsItem}>
                        <div className={classes.detailsLabel}>Connected host:</div>
                        <div className={classes.detailsValue}>{customerData?.opcHost}</div>
                    </div>
                </div>

                <div className={classes.detailsColumn}>
                    <div className={classes.detailsItem}>
                        <div className={classes.detailsLabel}>Connected as:</div>
                        <div className={classes.detailsValue}>{customerData?.opcUsername}</div>
                    </div>
                </div>
            </div>

            <div className={classes.actions}>
                {isLoading ? (
                    <div className={classes.statusPill}>
                        <LoaderSpinner className={classes.statusSpinner} color={'var(--text-secondary)'} />
                        <span>Checking connection</span>
                    </div>
                ) : null}

                {shouldShowConnectedState ? (
                    <div className={`${classes.statusPill} ${classes.connectedPill}`}>
                        <MuiIcon
                            icon={'check_circle'}
                            fontSize={'1.8rem'}
                            color={'var(--text-success)'}
                            type={'outlined'}
                        />
                        <span>Connected</span>
                    </div>
                ) : null}

                {shouldShowConnectionIssueState ? (
                    <div className={`${classes.statusPill} ${classes.connectionIssuePill}`}>
                        <MuiIcon
                            icon={'error_outline'}
                            fontSize={'1.8rem'}
                            color={'var(--text-warning)'}
                            type={'outlined'}
                        />
                        <span>Connection Issue</span>
                    </div>
                ) : null}

                <div className={classes.rightActions}>
                    <Button variant={'tertiary'} onClick={onReplaceCredentials}>
                        Replace Credentials
                    </Button>
                    <Button variant={'secondary'} onClick={() => refetch()}>
                        Test Connection
                    </Button>
                    <Button variant={'secondary'} destructive={true} onClick={() => setIsDisconnectModalOpen(true)}>
                        Disconnect
                    </Button>
                </div>
            </div>

            <ConfirmSaveModal
                isShowModal={isDisconnectModalOpen}
                onHideModal={() => setIsDisconnectModalOpen(false)}
                onConfirm={handleConfirmDisconnectOpc}
                isLoading={isDisconnectSubmitting}
                title={'Disconnect Oracle Primavera Cloud'}
                actionButtonLabel={'Yes'}
            >
                This will remove Oracle Primavera Cloud credentials from your customer profile and stop the integration
                until you connect again. Continue?
            </ConfirmSaveModal>
        </div>
    );
};
